import { Component, OnInit, Signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { WebsiteConfigFormExpansionPanelComponent } from '../website-config-form-expansion-panel/website-config-form-expansion-panel.component';
import { WebsiteStateService } from '../../../../../state-management/services/website-state.service';
import { BusinessConfiguration, SocialMediaLink } from '../../../../../state-management/model/interfaces/state.model';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { MatDividerModule } from '@angular/material/divider';
import { GenericDialog, DialogData } from '../website-config-form-page-administration/generic-dialog/generic-dialog.component';
import { firstValueFrom } from 'rxjs';
import { LoggerService } from '../../../../../services/logger.service';

interface SocialMediaPlatform {
    value: string;
    label: string;
    urlPrefix: string;
    placeholder: string;
}

@Component({
    selector: 'app-website-config-form-social-media',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        FormsModule,
        MatInputModule,
        MatCheckbox,
        MatSelectModule,
        MatTooltipModule,
        MatIcon,
        MatButtonModule,
        MatDividerModule,
        WebsiteConfigFormExpansionPanelComponent
    ],
    templateUrl: './website-config-form-social-media.component.html',
    styleUrl: './website-config-form-social-media.component.scss'
})
export class WebsiteConfigFormSocialMediaComponent implements OnInit {
    private formSubscription: Subscription = new Subscription();
    private debounceTimer: any;

    readonly dialog = inject(MatDialog);
    public metaData: Signal<BusinessConfiguration> = this.websiteState.configuration;
    public form!: FormGroup<{ socialMediaLinks: FormArray<FormGroup>; }>;

    // Verfügbare Social Media Plattformen
    public socialMediaPlatforms: SocialMediaPlatform[] = [
        { value: 'WhatsApp', label: 'WhatsApp', urlPrefix: 'https://wa.me/', placeholder: '491234567890' },
        { value: 'Facebook', label: 'Facebook', urlPrefix: 'https://facebook.com/', placeholder: 'deine-seite' },
        { value: 'Instagram', label: 'Instagram', urlPrefix: 'https://instagram.com/', placeholder: 'dein-konto' },
        { value: 'TikTok', label: 'TikTok', urlPrefix: 'https://tiktok.com/@', placeholder: 'dein-konto' },
        { value: 'Pinterest', label: 'Pinterest', urlPrefix: 'https://pinterest.com/', placeholder: 'dein-board' },
        { value: 'YouTube', label: 'YouTube', urlPrefix: 'https://youtube.com/', placeholder: 'dein-kanal' },
        { value: 'LinkedIn', label: 'LinkedIn', urlPrefix: 'https://linkedin.com/company/', placeholder: 'dein-konto' },
    ];

    constructor(
        private websiteState: WebsiteStateService,
        private logger: LoggerService
    ) { }

    ngOnInit(): void {
        const initialSocialMediaLinks = this.metaData().socialMediaLinks || [];
        this.logger.log('SOCIAL MEDIA FORM CHANGES: Initiale Social Media Links:', initialSocialMediaLinks);

        this.form = new FormGroup({
            socialMediaLinks: this.createSocialMediaLinksForm(initialSocialMediaLinks),
        });

        this.formSubscription = this.form.valueChanges.subscribe(() => {
            if (this.form.valid) {
                clearTimeout(this.debounceTimer);
                this.debounceTimer = setTimeout(async () => { this.#saveChanges(); }, 300);
            } else {
                this.logger.log('SOCIAL MEDIA FORM CHANGES: Formular ungültig, keine Daten gespeichert.', this.form);
            }
        });
    }

    ngOnDestroy() {
        if (this.formSubscription) {
            this.formSubscription.unsubscribe();
        }
    }

    private urlValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            if (!control.value) {
                return null; // Let required validator handle empty values
            }

            // Check for http/https protocol
            const hasValidProtocol = /^https?:\/\//.test(control.value);

            if (!hasValidProtocol) {
                return { invalidUrl: 'URL muss mit http:// oder https:// beginnen' };
            }

            // Check for valid domain ending - domain should have at least one dot and end with valid characters
            const hasValidDomain = /^https?:\/\/(.+)\.[a-z]{2,}(\/.*)?$/i.test(control.value);

            if (!hasValidDomain) {
                return { invalidUrl: 'URL muss mit einer gültigen Domain enden (z.B. .com, .de, .org)' };
            }

            return null;
        };
    }

    private platformSpecificValidator(platform: string): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            if (!control.value || !platform) {
                return null;
            }

            const url = control.value;

            switch (platform) {
                case 'WhatsApp':
                    // WhatsApp must start with https://wa.me/ and end with only digits
                    if (!url.startsWith('https://wa.me/')) {
                        return { invalidUrl: 'WhatsApp-Link muss mit https://wa.me/ beginnen' };
                    }
                    const phoneNumber = url.replace('https://wa.me/', '');
                    if (!/^\d+$/.test(phoneNumber)) {
                        return { invalidUrl: 'Nach https://wa.me/ dürfen nur Ziffern stehen (z.B. 491234567890)' };
                    }
                    if (phoneNumber.length < 10 || phoneNumber.length > 15) {
                        return { invalidUrl: 'Telefonnummer muss zwischen 10 und 15 Ziffern lang sein' };
                    }
                    break;

                case 'Facebook':
                    if (!url.startsWith('https://facebook.com/') && !url.startsWith('https://www.facebook.com/')) {
                        return { invalidUrl: 'Facebook-Link muss mit https://facebook.com/ oder https://www.facebook.com/ beginnen' };
                    }
                    break;

                case 'Instagram':
                    if (!url.startsWith('https://instagram.com/') && !url.startsWith('https://www.instagram.com/')) {
                        return { invalidUrl: 'Instagram-Link muss mit https://instagram.com/ oder https://www.instagram.com/ beginnen' };
                    }
                    break;

                case 'TikTok':
                    if (!url.startsWith('https://tiktok.com/@') && !url.startsWith('https://www.tiktok.com/@')) {
                        return { invalidUrl: 'TikTok-Link muss mit https://tiktok.com/@ oder https://www.tiktok.com/@ beginnen' };
                    }
                    break;

                case 'Pinterest':
                    if (!url.startsWith('https://pinterest.com/') && !url.startsWith('https://www.pinterest.com/')) {
                        return { invalidUrl: 'Pinterest-Link muss mit https://pinterest.com/ oder https://www.pinterest.com/ beginnen' };
                    }
                    break;

                case 'YouTube':
                    if (!url.startsWith('https://youtube.com/') && !url.startsWith('https://www.youtube.com/')) {
                        return { invalidUrl: 'YouTube-Link muss mit https://youtube.com/ oder https://www.youtube.com/ beginnen' };
                    }
                    break;

                case 'LinkedIn':
                    if (!url.startsWith('https://linkedin.com/') && !url.startsWith('https://www.linkedin.com/')) {
                        return { invalidUrl: 'LinkedIn-Link muss mit https://linkedin.com/ oder https://www.linkedin.com/ beginnen' };
                    }
                    break;
            }

            return null;
        };
    }

    private createSocialMediaLinksForm(socialMediaLinks: SocialMediaLink[]): FormArray {
        return new FormArray(socialMediaLinks.map(link => this.createSocialMediaLinkGroup(link)));
    }

    private createSocialMediaLinkGroup(link: SocialMediaLink): FormGroup {
        const group = new FormGroup({
            platform: new FormControl(link.platform || '', [Validators.required]),
            url: new FormControl(link.url || '', [Validators.required, this.urlValidator()]),
            isActive: new FormControl(link.isActive !== undefined ? link.isActive : true),
        });

        // Add platform-specific validator when platform changes
        group.get('platform')?.valueChanges.subscribe(platform => {
            const urlControl = group.get('url');
            if (urlControl && platform) {
                // Remove old platform-specific validator and add new one
                urlControl.setValidators([
                    Validators.required,
                    this.urlValidator(),
                    this.platformSpecificValidator(platform)
                ]);
                urlControl.updateValueAndValidity();
            } else if (urlControl) {
                // Remove platform-specific validator if no platform selected
                urlControl.setValidators([
                    Validators.required,
                    this.urlValidator()
                ]);
                urlControl.updateValueAndValidity();
            }
        });

        // Set initial platform-specific validator if platform is already set
        if (link.platform) {
            const urlControl = group.get('url');
            if (urlControl) {
                urlControl.setValidators([
                    Validators.required,
                    this.urlValidator(),
                    this.platformSpecificValidator(link.platform)
                ]);
            }
        }

        return group;
    }

    get socialMediaLinksArray(): FormArray<FormGroup> {
        return this.form.get('socialMediaLinks') as FormArray<FormGroup>;
    }

    public addSocialMediaLink() {
        this.socialMediaLinksArray.push(this.createSocialMediaLinkGroup({
            platform: '',
            url: '',
            isActive: true,
        }));
    }

    public removeSocialMediaLinkAtIndex(index: number): void {
        const formArray = this.socialMediaLinksArray as FormArray;
        if (index >= 0 && index < formArray.length) {
            formArray.removeAt(index);
            this.form.updateValueAndValidity();
            this.form.markAsDirty();
            // This forces Angular to run change detection in the next cycle
            setTimeout(() => { }, 0);
        }
    }

    async onDeleteSocialMediaLink(index: number): Promise<void> {
        const link: SocialMediaLink = this.socialMediaLinksArray.value[index];
        try {
            const confirmDelete = await this.openDeleteSocialMediaDialog(link.platform);
            if (confirmDelete) {
                this.removeSocialMediaLinkAtIndex(index);
            } else {
                this.logger.log(`Löschen des Social Media Links ${link.platform} abgebrochen`);
            }
        } catch (error) {
            this.logger.error('Fehler beim Öffnen des Social Media Delete-Dialogs:', error);
        }
    }

    private openDeleteSocialMediaDialog(platform: string): Promise<boolean> {
        const data: DialogData = {
            title: 'Warnung',
            textChunks: [
                { text: 'Soll der Social Media Link für ' },
                { text: ` ${platform} `, isStrong: true },
                { text: ' wirklich gelöscht werden?' }
            ],
            cancelButtonText: 'Abbrechen',
            confirmButtonText: 'Ja, löschen!',
        };

        const dialogRef = this.dialog.open(GenericDialog, {
            data: data,
            panelClass: "admin-theme"
        });
        return firstValueFrom(dialogRef.afterClosed());
    }

    // Helper method to get URL prefix for selected platform
    public onPlatformChange(formGroup: FormGroup, selectedPlatform: string): void {
        const platform = this.socialMediaPlatforms.find(p => p.value === selectedPlatform);
        if (platform) {
            const currentUrl = formGroup.get('url')?.value || '';
            // Only set prefix if URL is empty or just contains a prefix
            if (!currentUrl || currentUrl === 'https://' || this.socialMediaPlatforms.some(p => currentUrl === p.urlPrefix)) {
                formGroup.get('url')?.setValue(platform.urlPrefix);
            }
        }
    }

    // Get placeholder for current platform
    public getPlaceholderForPlatform(platform: string): string {
        const platformData = this.socialMediaPlatforms.find(p => p.value === platform);
        return platformData ? platformData.placeholder : 'dein-konto';
    }

    // Get URL prefix for display
    public getUrlPrefixForPlatform(platform: string): string {
        const platformData = this.socialMediaPlatforms.find(p => p.value === platform);
        return platformData ? platformData.urlPrefix : 'https://';
    }

    // Get platform icon for display
    public getPlatformIcon(platform: string): string {
        const iconMap: { [key: string]: string } = {
            'WhatsApp': 'chat',
            'Facebook': 'facebook',
            'Instagram': 'camera_alt',
            'TikTok': 'music_note',
            'Pinterest': 'push_pin',
            'YouTube': 'play_circle',
            'LinkedIn': 'work',
        };
        return iconMap[platform] || 'link';
    }

    #saveChanges() {
        const socialMediaLinks = this.socialMediaLinksArray.value;
        this.websiteState.updateConfiguration({ socialMediaLinks });
    }
}